import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/shared/AuthContext';
import PageContainer from '../components/common/PageContainer';
import PageHeader from '../components/common/PageHeader';
import PageSection from '../components/common/PageSection';
import TokenPurchase from '../components/common/TokenPurchase';

const PatientSettings: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [settings, setSettings] = useState({
        account: {
            displayName: user?.displayName || '',
            email: user?.email || '',
        },
        aiPreferences: {
            modelChoice: 'balanced',
            conversationMemory: true,
            tonePreference: 'supportive',
            guidedInterventions: true
        },
        privacy: {
            dataStorage: 'local',
            analytics: false,
            personalization: true
        },
        notifications: {
            emailSummaries: true,
            weeklyInsights: true,
            sessionReminders: true
        },
        appearance: {
            theme: 'system',
            fontSize: 'medium'
        }
    });

    const handleSettingChange = (category: string, setting: string, value: any) => {
        setSettings(prev => ({
            ...prev,
            [category]: {
                ...prev[category as keyof typeof prev],
                [setting]: value
            }
        }));
    };

    const handleSave = () => {
        alert('Settings saved successfully!');
    };

    const handleDeleteAccount = () => {
        if (confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
            alert('Delete account functionality coming soon');
        }
    };

    const handleExportData = () => {
        alert('Export data functionality coming soon');
    };

    const handleDeleteData = () => {
        if (confirm('Are you sure you want to delete all your data? This action cannot be undone.')) {
            alert('Delete data functionality coming soon');
        }
    };

    return (
        <PageContainer>
            <PageHeader 
                title="Patient Settings" 
                emoji="⚙️"
                description="Customize your app experience and preferences"
            />

            {/* Account Section */}
            <PageSection title="Account" emoji="👤">
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Display Name
                        </label>
                        <input
                            type="text"
                            value={settings.account.displayName}
                            onChange={(e) => handleSettingChange('account', 'displayName', e.target.value)}
                            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Enter your display name"
                        />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email Address
                        </label>
                        <input
                            type="email"
                            value={settings.account.email}
                            disabled
                            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                    </div>

                    <div className="flex space-x-4">
                        <button
                            onClick={() => alert('Password reset functionality coming soon')}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                        >
                            Reset Password
                        </button>
                        <button
                            onClick={handleDeleteAccount}
                            className="px-4 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
                        >
                            Delete Account
                        </button>
                    </div>
                </div>
            </PageSection>

            {/* Token Management */}
            <PageSection title="Token Management" emoji="🪙">
                <TokenPurchase />
            </PageSection>

            {/* AI Preferences */}
            <PageSection title="AI Preferences" emoji="🤖">
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Model Choice
                        </label>
                        <select
                            value={settings.aiPreferences.modelChoice}
                            onChange={(e) => handleSettingChange('aiPreferences', 'modelChoice', e.target.value)}
                            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="balanced">Balanced</option>
                            <option value="reflective">Reflective</option>
                            <option value="direct">Direct</option>
                        </select>
                    </div>

                    <div className="flex items-center justify-between">
                        <div>
                            <label className="font-medium text-gray-900">Conversation Memory</label>
                            <p className="text-sm text-gray-600">Remember recent sessions</p>
                        </div>
                        <input
                            type="checkbox"
                            checked={settings.aiPreferences.conversationMemory}
                            onChange={(e) => handleSettingChange('aiPreferences', 'conversationMemory', e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Tone Preference
                        </label>
                        <select
                            value={settings.aiPreferences.tonePreference}
                            onChange={(e) => handleSettingChange('aiPreferences', 'tonePreference', e.target.value)}
                            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="supportive">Supportive</option>
                            <option value="neutral">Neutral</option>
                            <option value="clinical">Clinical</option>
                        </select>
                    </div>

                    <div className="flex items-center justify-between">
                        <div>
                            <label className="font-medium text-gray-900">Guided Interventions</label>
                            <p className="text-sm text-gray-600">Enable guided interventions</p>
                        </div>
                        <input
                            type="checkbox"
                            checked={settings.aiPreferences.guidedInterventions}
                            onChange={(e) => handleSettingChange('aiPreferences', 'guidedInterventions', e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                    </div>
                </div>
            </PageSection>

            {/* Privacy & Data */}
            <PageSection title="Privacy & Data" emoji="🔒">
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Data Storage
                        </label>
                        <select
                            value={settings.privacy.dataStorage}
                            onChange={(e) => handleSettingChange('privacy', 'dataStorage', e.target.value)}
                            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="local">Store locally only</option>
                            <option value="cloud">Store in cloud</option>
                        </select>
                    </div>

                    <div className="flex items-center justify-between">
                        <div>
                            <label className="font-medium text-gray-900">Analytics</label>
                            <p className="text-sm text-gray-600">Allow analytics data collection</p>
                        </div>
                        <input
                            type="checkbox"
                            checked={settings.privacy.analytics}
                            onChange={(e) => handleSettingChange('privacy', 'analytics', e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                    </div>

                    <div className="flex items-center justify-between">
                        <div>
                            <label className="font-medium text-gray-900">Personalization</label>
                            <p className="text-sm text-gray-600">Allow personalization features</p>
                        </div>
                        <input
                            type="checkbox"
                            checked={settings.privacy.personalization}
                            onChange={(e) => handleSettingChange('privacy', 'personalization', e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                    </div>

                    <div className="flex space-x-4">
                        <button
                            onClick={handleExportData}
                            className="px-4 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
                        >
                            Export My Data
                        </button>
                        <button
                            onClick={handleDeleteData}
                            className="px-4 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
                        >
                            Delete My Data
                        </button>
                    </div>
                </div>
            </PageSection>

            {/* Notifications */}
            <PageSection title="Notifications" emoji="🔔">
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <label className="font-medium text-gray-900">Email Summaries</label>
                            <p className="text-sm text-gray-600">Receive email summaries</p>
                        </div>
                        <input
                            type="checkbox"
                            checked={settings.notifications.emailSummaries}
                            onChange={(e) => handleSettingChange('notifications', 'emailSummaries', e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                    </div>

                    <div className="flex items-center justify-between">
                        <div>
                            <label className="font-medium text-gray-900">Weekly Insights</label>
                            <p className="text-sm text-gray-600">Receive weekly insight reports</p>
                        </div>
                        <input
                            type="checkbox"
                            checked={settings.notifications.weeklyInsights}
                            onChange={(e) => handleSettingChange('notifications', 'weeklyInsights', e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                    </div>

                    <div className="flex items-center justify-between">
                        <div>
                            <label className="font-medium text-gray-900">Session Reminders</label>
                            <p className="text-sm text-gray-600">Receive session reminders</p>
                        </div>
                        <input
                            type="checkbox"
                            checked={settings.notifications.sessionReminders}
                            onChange={(e) => handleSettingChange('notifications', 'sessionReminders', e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                    </div>
                </div>
            </PageSection>

            {/* Appearance */}
            <PageSection title="Appearance" emoji="🎨">
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Theme
                        </label>
                        <select
                            value={settings.appearance.theme}
                            onChange={(e) => handleSettingChange('appearance', 'theme', e.target.value)}
                            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="light">Light</option>
                            <option value="dark">Dark</option>
                            <option value="system">System</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Font Size
                        </label>
                        <select
                            value={settings.appearance.fontSize}
                            onChange={(e) => handleSettingChange('appearance', 'fontSize', e.target.value)}
                            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="small">Small</option>
                            <option value="medium">Medium</option>
                            <option value="large">Large</option>
                        </select>
                    </div>
                </div>
            </PageSection>

            {/* Shared Sections */}
            <PageSection title="Shared Settings" emoji="🌐">
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Language
                        </label>
                        <select
                            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="en">English</option>
                            <option value="es">Spanish</option>
                            <option value="fr">French</option>
                        </select>
                    </div>

                    <div className="flex space-x-4">
                        <button
                            onClick={() => alert('Password change functionality coming soon')}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                        >
                            Change Password
                        </button>
                        <button
                            onClick={() => alert('Logout functionality coming soon')}
                            className="px-4 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
                        >
                            Logout
                        </button>
                    </div>

                    <div className="flex space-x-4 text-sm">
                        <a href="/legal" className="text-blue-600 hover:text-blue-800">Terms of Service</a>
                        <a href="/legal" className="text-blue-600 hover:text-blue-800">Privacy Policy</a>
                        <a href="#" className="text-blue-600 hover:text-blue-800">Support</a>
                    </div>
                </div>
            </PageSection>

            {/* Save Button */}
            <PageSection title="Save Changes" emoji="💾">
                <div className="flex justify-end space-x-4">
                    <button 
                        onClick={() => navigate('/dashboard')}
                        className="px-6 py-2 border border-gray-300 text-gray-900 bg-white rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button 
                        onClick={handleSave}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Save Settings
                    </button>
                </div>
            </PageSection>
        </PageContainer>
    );
};

export default PatientSettings;